import React from 'react';
import { FiShoppingCart, FiStar } from 'react-icons/fi';
import { formatCurrency } from '../../utils/formatters';

const ProductCard = ({ product, onAddToCart, onClick }) => {
  const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDODkuNTA0NiA3MCA4MSA3OC41MDQ2IDgxIDg5QzgxIDk5LjQ5NTQgODkuNTA0NiAxMDggMTAwIDEwOEMxMTAuNDk1IDEwOCAxMTkgOTkuNDk1NCAxMTkgODlDMTE5IDc4LjUwNDYgMTEwLjQ5NSA3MCAxMDAgNzBaIiBmaWxsPSIjOUI5QkEzIi8+CjxwYXRoIGQ9Ik02NSAxMzBDNjUgMTE5LjUwNyA3My41MDY2IDExMSA4NCAxMTFIMTE2QzEyNi40OTMgMTExIDEzNSAxMTkuNTA3IDEzNSAxMzBWMTMwSDY1VjEzMFoiIGZpbGw9IiM5QjlCQTMiLz4KPC9zdmc+';
  
  // Support both Cloudinary URLs, object format {url, public_id}, and legacy Base64 images
  const getImageSrc = () => {
    if (!product.photos || product.photos.length === 0) return defaultImage;
    const photo = product.photos[0];
    
    // Handle object format {url, public_id}
    if (typeof photo === 'object' && photo?.url) return photo.url;
    
    // Handle string formats
    if (typeof photo === 'string') {
      if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
      if (photo.startsWith('data:')) return photo;
      return `data:image/jpeg;base64,${photo}`;
    }
    
    return defaultImage;
  };
  
  const image = getImageSrc();

  return (
    <div 
      className="bg-white rounded-xl shadow-md overflow-hidden card-hover cursor-pointer animate-fadeIn group"
      onClick={onClick}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative overflow-hidden">
        <img
          src={image}
          alt={product.name}
          className="w-full h-44 object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        
        {/* Condition Badge */}
        <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-medium rounded-full shadow-sm ${
          product.condition === 'novo' 
            ? 'bg-green-500 text-white' 
            : 'bg-amber-500 text-white'
        }`}>
          {product.condition === 'novo' ? 'Novo' : 'Usado'}
        </span>
        
        {/* Stock Badge */}
        {product.stock <= 3 && product.stock > 0 && (
          <span className="absolute top-2 right-2 px-2 py-1 text-xs font-medium rounded-full bg-red-500 text-white shadow-sm">
            Últimas {product.stock}!
          </span>
        )}
        
        {/* Out of Stock Overlay */}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="px-4 py-2 bg-red-600 text-white font-bold rounded-lg">
              ESGOTADO
            </span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* Store Name */}
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            {product.store_name}
          </p>
          {product.store_is_open !== undefined && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              product.store_is_open 
                ? 'bg-green-100 text-green-700' 
                : 'bg-orange-100 text-orange-700'
            }`}>
              {product.store_is_open ? 'Aberta' : 'Fechada'}
            </span>
          )}
        </div>
        
        {/* Product Name */}
        <h3 className="font-semibold text-gray-800 line-clamp-2 min-h-[2.5rem] leading-tight">
          {product.name}
        </h3>
        
        {/* Rating */}
        {product.store_rating > 0 && (
          <div className="flex items-center gap-1 mt-2">
            <FiStar className="text-yellow-400 fill-current" size={14} />
            <span className="text-sm text-gray-600">{product.store_rating.toFixed(1)}</span>
          </div>
        )}
        
        {/* Price & Action */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div>
            <span className="text-xl font-bold text-[#DC143C]">
              {formatCurrency(product.price)}
            </span>
            <p className="text-xs text-gray-400">
              ou 12x de {formatCurrency(product.price / 12)}
            </p>
          </div>
          
          {onAddToCart && product.stock > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(product);
              }}
              className="p-3 bg-[#DC143C] text-white rounded-full hover:bg-[#B01030] transition-all hover:scale-110 shadow-lg"
              data-testid={`add-to-cart-${product.id}`}
              aria-label="Adicionar ao carrinho"
            >
              <FiShoppingCart size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
